import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CurrencyInput,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { PickField } from './pick-field.js';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilLine, Plus, RotateCcw, ShoppingCart, Archive } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  getApiError,
  purchasingApi,
  type Currency,
  type SupplierOffering,
  type SupplierOfferingKind,
  type SupplierOfferingSort,
} from '../../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { PurchasingView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  activityOptions,
  currencyOptions,
  offeringKindFilterOptions,
  offeringKindLabels,
  offeringKindOptions,
  shortUnit,
  trimQuantity,
  unitCostLabel,
  type PurchasingCapabilities,
} from './shared.js';

const offeringSchema = z.object({
  kind: z.enum(['PRODUCT', 'SERVICE']),
  supplierId: z.string().min(1, 'Escolha o fornecedor.'),
  productId: z.string(),
  packageId: z.string(),
  displayName: z
    .string()
    .trim()
    .min(2, 'Informe o nome da oferta.')
    .max(160, 'Use até 160 caracteres.'),
  supplierCode: z.string().trim().max(80, 'Use até 80 caracteres.'),
  unitPriceCents: z.number().int().positive('Informe um preço maior que zero.'),
  currency: z.enum(['BRL', 'USD', 'ARS']),
  minimumQuantity: z.string(),
  leadTimeDays: z.string(),
  notes: z.string().trim().max(1000, 'Use até 1000 caracteres.'),
});
type OfferingForm = z.infer<typeof offeringSchema>;

const NONE = '';
const columns: Array<{ sort: SupplierOfferingSort; label: string }> = [
  { sort: 'displayName', label: 'Oferta' },
  { sort: 'supplierDisplayName', label: 'Fornecedor' },
  { sort: 'unitPriceCents', label: 'Preço' },
];

export function OfferingsPanel({
  capabilities,
  view,
  onView,
}: {
  capabilities: PurchasingCapabilities;
  view: PurchasingView;
  onView: ViewSetter<PurchasingView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [activationTarget, setActivationTarget] = useState<SupplierOffering>();
  const [activationError, setActivationError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    activity: view.activity,
    kind: view.offeringKind,
    ...(view.supplier ? { supplierId: view.supplier } : {}),
    sort: view.offeringSort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'purchasing', 'offerings', filters],
    queryFn: () => purchasingApi.listOfferings(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['company', 'purchasing', 'offerings', view.offering],
    queryFn: () => purchasingApi.getOffering(view.offering!),
    enabled: Boolean(view.offering),
    retry: false,
  });
  const suppliers = useQuery({
    queryKey: ['company', 'purchasing', 'supplier-options'],
    queryFn: () =>
      purchasingApi.listSuppliers({
        activity: 'ACTIVE',
        kind: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    retry: false,
  });

  function toggleSort(column: SupplierOfferingSort) {
    if (view.offeringSort === column) {
      onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    } else onView({ offeringSort: column, direction: 'asc' });
  }

  async function setActive(offering: SupplierOffering) {
    setActivationError(undefined);
    try {
      await purchasingApi.setOfferingActive(offering.id, !offering.active);
      await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
      setActivationTarget(undefined);
      snackbar.success(offering.active ? 'Oferta arquivada.' : 'Oferta reativada.');
    } catch (error) {
      setActivationError(getApiError(error));
    }
  }

  const supplierOptions = [
    { value: NONE, label: 'Todos os fornecedores' },
    ...(suppliers.data?.suppliers ?? []).map((item) => ({
      value: item.id,
      label: item.displayName,
    })),
  ];
  const total = page.data?.total ?? 0;
  const chips = [
    ...(view.activity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityOptions.find((item) => item.value === view.activity)!.label,
            onRemove: () => onView({ activity: 'ALL' }),
          },
        ]),
    ...(view.offeringKind === 'ALL'
      ? []
      : [
          {
            key: 'offeringKind',
            label: offeringKindFilterOptions.find((item) => item.value === view.offeringKind)!
              .label,
            onRemove: () => onView({ offeringKind: 'ALL' }),
          },
        ]),
    ...(view.supplier
      ? [
          {
            key: 'supplier',
            label:
              supplierOptions.find((item) => item.value === view.supplier)?.label ?? 'Fornecedor',
            onRemove: () => onView({ supplier: '' }),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="mb-5 flex justify-end">
        {capabilities.canManageOfferings ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Cadastrar oferta
          </Button>
        ) : null}
      </div>

      <FilterBar
        searchId="purchasing-offering-search"
        searchLabel="Buscar oferta"
        searchPlaceholder="Oferta, código do fornecedor ou produto"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: 'ALL', offeringKind: 'ALL', supplier: '' })}
        count={`${total} ${total === 1 ? 'oferta' : 'ofertas'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'oferta' : 'ofertas'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar ofertas por situação"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <FilterGroup
          label="Espécie"
          description="Filtrar ofertas por produto ou serviço"
          options={offeringKindFilterOptions}
          value={view.offeringKind}
          onChange={(offeringKind) => onView({ offeringKind })}
        />
        <div className="min-w-0">
          <PickField
            label="Fornecedor"
            placeholder="Todos os fornecedores"
            value={view.supplier}
            emptyValue={NONE}
            options={supplierOptions}
            onChange={(supplier) => onView({ supplier })}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando ofertas" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={ShoppingCart}
          title="Nenhuma oferta encontrada"
          description="Ajuste os filtros ou registre o que um fornecedor vende e por quanto."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label="Ordenação das ofertas"
                columns={columns}
                sort={view.offeringSort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.offerings.map((offering) => (
                  <li key={offering.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                        <ShoppingCart className="size-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <strong className="block truncate text-sm">{offering.displayName}</strong>
                        <span className="mt-1 block truncate text-xs text-content-muted">
                          {offering.supplierDisplayName}
                        </span>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-content-muted">
                          <span>{formatCurrency(offering.unitPriceCents, offering.currency)}</span>
                          <span>{purchaseUnitOf(offering)}</span>
                        </div>
                        {offering.active ? null : (
                          <div className="mt-2">
                            <StatusPill tone="neutral">Arquivada</StatusPill>
                          </div>
                        )}
                      </div>
                      <Actions
                        offering={offering}
                        capabilities={capabilities}
                        onEdit={() => onView({ offering: offering.id })}
                        onActivation={() => setActivationTarget(offering)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Ofertas comerciais dos fornecedores</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.offeringSort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Unidade de compra
                  </th>
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Condições
                  </th>
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.offerings.map((offering) => (
                  <tr key={offering.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">{offering.displayName}</strong>
                      <span className="mt-1 block truncate text-xs text-content-muted">
                        {offering.productDisplayName ?? offeringKindLabels[offering.kind]}
                      </span>
                      {offering.active ? null : <StatusPill tone="neutral">Arquivada</StatusPill>}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {offering.supplierDisplayName}
                      {offering.supplierCode ? (
                        <span className="mt-1 block truncate">{offering.supplierCode}</span>
                      ) : null}
                    </td>
                    <td className="px-5 py-4 text-xs">
                      <span className="block font-semibold text-content">
                        {formatCurrency(offering.unitPriceCents, offering.currency)}
                      </span>
                      {unitCostLabel(offering) ? (
                        <span className="mt-1 block text-content-muted">
                          {unitCostLabel(offering)} por unidade
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {purchaseUnitOf(offering)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {conditionsOf(offering)}
                    </td>
                    <td className="px-3 py-4">
                      <Actions
                        offering={offering}
                        capabilities={capabilities}
                        onEdit={() => onView({ offering: offering.id })}
                        onActivation={() => setActivationTarget(offering)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'oferta' : 'ofertas')}
          />
        </div>
      )}

      <OfferingDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        offering={null}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
          setCreateOpen(false);
          snackbar.success('Oferta cadastrada.');
        }}
      />
      <OfferingDialog
        open={Boolean(view.offering)}
        onOpenChange={(open) => {
          if (!open) onView({ offering: undefined });
        }}
        offering={detail.data ?? null}
        loading={detail.isLoading}
        {...(detail.error ? { error: getApiError(detail.error) } : {})}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
          onView({ offering: undefined });
          snackbar.success('Oferta atualizada.');
        }}
      />
      {activationTarget ? (
        <Modal
          open
          onOpenChange={() => setActivationTarget(undefined)}
          title={activationTarget.active ? 'Arquivar oferta' : 'Reativar oferta'}
          description={
            activationTarget.active
              ? 'A oferta sai da negociação, mas o cadastro permanece.'
              : 'A oferta volta a ficar disponível para novos pedidos.'
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setActivationTarget(undefined)}>
                Cancelar
              </Button>
              <Button onClick={() => void setActive(activationTarget)}>
                {activationTarget.active ? 'Arquivar oferta' : 'Reativar oferta'}
              </Button>
            </>
          }
        >
          <p className="text-sm">
            {activationTarget.active ? 'Arquivar' : 'Reativar'}{' '}
            <strong>{activationTarget.displayName}</strong>?
          </p>
          {activationError ? <RequestAlert message={activationError} /> : null}
        </Modal>
      ) : null}
    </>
  );
}

/// O que uma unidade de compra é: a embalagem com a conversão que ela tinha quando a oferta nasceu,
/// ou a própria unidade-base.
function purchaseUnitOf(offering: SupplierOffering): string {
  if (offering.kind === 'SERVICE') return 'Serviço contratado';
  if (!offering.packageDisplayName) return 'Unidade-base do produto';
  return offering.packageBaseQuantity
    ? `${offering.packageDisplayName} · ${trimQuantity(offering.packageBaseQuantity)}`
    : offering.packageDisplayName;
}

function conditionsOf(offering: SupplierOffering): string {
  const parts = [
    offering.minimumQuantity ? `Mínimo ${offering.minimumQuantity}` : null,
    offering.leadTimeDays === null
      ? null
      : offering.leadTimeDays === 0
        ? 'Entrega imediata'
        : `${offering.leadTimeDays} ${offering.leadTimeDays === 1 ? 'dia' : 'dias'}`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Sem condição';
}

function Actions({
  offering,
  capabilities,
  onEdit,
  onActivation,
}: {
  offering: SupplierOffering;
  capabilities: PurchasingCapabilities;
  onEdit: () => void;
  onActivation: () => void;
}) {
  if (!capabilities.canManageOfferings) return null;
  return (
    <div className="flex justify-end gap-1">
      <IconButton label="Editar oferta" icon={<PencilLine className="size-4" />} onClick={onEdit} />
      <IconButton
        label={offering.active ? 'Arquivar' : 'Reativar'}
        {...(offering.active ? { tone: 'danger' as const } : {})}
        icon={offering.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />}
        onClick={onActivation}
      />
    </div>
  );
}

/// O `Select` do sistema não tem campo de erro nem de dica. Em vez de improvisar um nativo, a
/// mensagem vem colada embaixo dele, com o mesmo papel de leitura.
function Field({ message, children }: { message?: string | undefined; children: ReactNode }) {
  return (
    <div className="min-w-0">
      {children}
      {message ? (
        <p role="alert" className="mt-1 text-xs text-danger">
          {message}
        </p>
      ) : null}
    </div>
  );
}

function OfferingDialog({
  open,
  onOpenChange,
  offering,
  loading = false,
  error,
  capabilities,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offering: SupplierOffering | null;
  loading?: boolean;
  error?: string;
  capabilities: PurchasingCapabilities;
  onSaved: () => Promise<void>;
}) {
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<OfferingForm>({
    resolver: zodResolver(offeringSchema),
    defaultValues: emptyOffering(),
  });
  const kind = useWatch({ control: form.control, name: 'kind' });
  const supplierId = useWatch({ control: form.control, name: 'supplierId' });
  const productId = useWatch({ control: form.control, name: 'productId' });
  const packageId = useWatch({ control: form.control, name: 'packageId' });
  const currency = useWatch({ control: form.control, name: 'currency' });
  const unitPriceCents = useWatch({ control: form.control, name: 'unitPriceCents' });

  const suppliers = useQuery({
    queryKey: ['company', 'purchasing', 'supplier-options'],
    queryFn: () =>
      purchasingApi.listSuppliers({
        activity: 'ACTIVE',
        kind: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    enabled: open,
    retry: false,
  });
  /// O catálogo comprável atravessa a porta do Estoque. Vir vazio é resposta legítima — quer dizer
  /// que a empresa não contratou Estoque —, e a tela explica em vez de oferecer um campo morto.
  const catalog = useQuery({
    queryKey: ['company', 'purchasing', 'purchasable-products'],
    queryFn: () => purchasingApi.listPurchasableProducts(),
    enabled: open,
    retry: false,
  });

  useEffect(() => {
    if (open) form.reset(offering ? formOf(offering) : emptyOffering());
  }, [form, open, offering]);

  useEffect(() => {
    if (open && !offering && catalog.isSuccess && !catalog.data.available) {
      form.setValue('kind', 'SERVICE');
      form.setValue('productId', NONE);
      form.setValue('packageId', NONE);
    }
  }, [catalog.data, catalog.isSuccess, form, offering, open]);

  function changeOpen(next: boolean) {
    if (!next) setRequestError(undefined);
    onOpenChange(next);
  }

  async function save(data: OfferingForm) {
    setRequestError(undefined);
    const payload = {
      kind: data.kind,
      productId: data.kind === 'PRODUCT' ? data.productId || null : null,
      packageId: data.kind === 'PRODUCT' ? data.packageId || null : null,
      displayName: data.displayName,
      supplierCode: data.supplierCode || null,
      unitPriceCents: data.unitPriceCents,
      currency: data.currency,
      minimumQuantity: data.minimumQuantity ? Number(data.minimumQuantity) : null,
      leadTimeDays: data.leadTimeDays ? Number(data.leadTimeDays) : null,
      notes: data.notes || null,
    };
    try {
      if (offering) await purchasingApi.updateOffering(offering.id, payload);
      else await purchasingApi.createOffering({ ...payload, supplierId: data.supplierId });
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  const products = catalog.data?.products ?? [];
  const inventoryAvailable = catalog.data?.available ?? false;
  const chosen = products.find((item) => item.id === productId);
  const productOptions = [
    { value: NONE, label: 'Escolha o produto' },
    ...products.map((item) => ({ value: item.id, label: item.displayName })),
  ];
  const packageOptions = [
    {
      value: NONE,
      label: chosen ? `Unidade-base (${shortUnit(chosen.baseUnit)})` : 'Unidade-base',
    },
    ...(chosen?.packages ?? []).map((item) => ({
      value: item.id,
      label: `${item.displayName} · ${trimQuantity(item.baseQuantity)} ${shortUnit(chosen!.baseUnit)}`,
    })),
  ];
  const supplierOptions = [
    { value: NONE, label: 'Escolha o fornecedor' },
    ...(suppliers.data?.suppliers ?? []).map((item) => ({
      value: item.id,
      label: item.displayName,
    })),
  ];

  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title={offering ? 'Editar oferta' : 'Cadastrar oferta'}
      description="O que um fornecedor vende e em que condições. O preço é o de uma unidade de compra."
      footer={
        !loading && !error && capabilities.canManageOfferings ? (
          <>
            <FormErrorSummary
              form={'purchasing-offering-form'}
              className="basis-full sm:basis-auto sm:flex-1"
            />
            <Button type="button" variant="outline" onClick={() => changeOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="purchasing-offering-form"
              loading={form.formState.isSubmitting}
            >
              {offering ? 'Salvar oferta' : 'Cadastrar oferta'}
            </Button>
          </>
        ) : undefined
      }
    >
      {loading ? (
        <LoadingCard label="Carregando oferta" />
      ) : error ? (
        <ErrorCard message={error} />
      ) : (
        <form
          id="purchasing-offering-form"
          noValidate
          onSubmit={(event) => void form.handleSubmit(save)(event)}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {offering ? (
              <Input label="Fornecedor" value={offering.supplierDisplayName} readOnly disabled />
            ) : (
              <PickField
                error={form.formState.errors.supplierId?.message}
                label="Fornecedor"
                placeholder="Buscar fornecedor"
                value={supplierId}
                emptyValue={NONE}
                options={supplierOptions}
                onChange={(value) => form.setValue('supplierId', value, { shouldValidate: true })}
              />
            )}
            <Field
              message={
                !inventoryAvailable
                  ? 'Só ofertas de serviço: produtos exigem o módulo Estoque contratado.'
                  : products.length === 0
                    ? 'Cadastre um produto ativo no Estoque antes de criar uma oferta de produto.'
                    : undefined
              }
            >
              <Select
                label="Espécie"
                value={kind}
                options={
                  inventoryAvailable
                    ? offeringKindOptions
                    : offeringKindOptions.filter((item) => item.value === 'SERVICE')
                }
                onValueChange={(value) => {
                  form.setValue('kind', value as SupplierOfferingKind, { shouldValidate: true });
                  if (value === 'SERVICE') {
                    form.setValue('productId', NONE);
                    form.setValue('packageId', NONE);
                  }
                }}
              />
            </Field>

            {kind === 'PRODUCT' ? (
              <>
                <Field>
                  <PickField
                    label="Produto de estoque"
                    placeholder="Buscar produto"
                    value={productId}
                    emptyValue={NONE}
                    options={productOptions}
                    onChange={(value) => {
                      form.setValue('productId', value, { shouldValidate: true });
                      form.setValue('packageId', NONE);
                      const picked = products.find((item) => item.id === value);
                      if (picked && !form.getValues('displayName')) {
                        form.setValue('displayName', picked.displayName, { shouldValidate: true });
                      }
                    }}
                  />
                </Field>
                <Field>
                  <Select
                    label="Embalagem comprada"
                    value={packageId}
                    options={packageOptions}
                    onValueChange={(value) => form.setValue('packageId', value)}
                    disabled={!chosen}
                  />
                </Field>
              </>
            ) : null}

            <div className="sm:col-span-2">
              <Input
                label="Como o fornecedor chama"
                error={form.formState.errors.displayName?.message}
                {...form.register('displayName')}
              />
            </div>
            <Input
              label="Código no fornecedor (opcional)"
              error={form.formState.errors.supplierCode?.message}
              {...form.register('supplierCode')}
            />
            <Field>
              <Select
                label="Moeda"
                value={currency}
                options={currencyOptions}
                onValueChange={(value) =>
                  form.setValue('currency', value as Currency, { shouldValidate: true })
                }
              />
            </Field>
            <div className="sm:col-span-2">
              <CurrencyInput
                label="Preço de uma unidade de compra"
                currency={currency}
                value={unitPriceCents}
                onValueChange={(value) =>
                  form.setValue('unitPriceCents', value ?? 0, { shouldValidate: true })
                }
                error={form.formState.errors.unitPriceCents?.message}
                hint={
                  kind === 'PRODUCT' && packageId
                    ? 'O preço da embalagem inteira, não o da unidade-base.'
                    : 'O preço de uma unidade do que se compra.'
                }
              />
            </div>
            <Input
              label="Pedido mínimo (opcional)"
              inputMode="numeric"
              error={form.formState.errors.minimumQuantity?.message}
              {...form.register('minimumQuantity')}
            />
            <Input
              label="Prazo de entrega em dias (opcional)"
              inputMode="numeric"
              error={form.formState.errors.leadTimeDays?.message}
              {...form.register('leadTimeDays')}
            />
            <div className="sm:col-span-2">
              <Textarea
                label="Observação"
                rows={3}
                error={form.formState.errors.notes?.message}
                {...form.register('notes')}
              />
            </div>
          </div>

          {requestError ? <RequestAlert message={requestError} /> : null}
        </form>
      )}
    </Modal>
  );
}

function emptyOffering(): OfferingForm {
  return {
    kind: 'PRODUCT',
    supplierId: NONE,
    productId: NONE,
    packageId: NONE,
    displayName: '',
    supplierCode: '',
    unitPriceCents: 0,
    currency: 'BRL',
    minimumQuantity: '',
    leadTimeDays: '',
    notes: '',
  };
}

function formOf(offering: SupplierOffering): OfferingForm {
  return {
    kind: offering.kind,
    supplierId: offering.supplierId,
    productId: offering.productId ?? NONE,
    packageId: offering.packageId ?? NONE,
    displayName: offering.displayName,
    supplierCode: offering.supplierCode ?? '',
    unitPriceCents: offering.unitPriceCents,
    currency: offering.currency,
    minimumQuantity: offering.minimumQuantity?.toString() ?? '',
    leadTimeDays: offering.leadTimeDays?.toString() ?? '',
    notes: offering.notes ?? '',
  };
}
